/**
 * ShortlistTable — risk-colored program table inside the artifact panel.
 *
 * Features:
 * - Visual coverage stats bar with color segments
 * - Deadline urgency badge (URGENT / SOON / OK / UNKNOWN)
 * - Eligibility gap warning chip with hover detail
 * - Collapsible "Why?" section (rationale, evidence quote, source URL)
 * - Scholarship badge
 */

import { useState } from "react";
import type { ProgramEvaluation, ShortlistData } from "@/api/types";
import { Badge } from "@/components/common/Badge";

interface Props {
  data: ShortlistData;
}

// --- Deadline urgency helpers ---

type Urgency = "urgent" | "soon" | "ok" | "unknown";

function computeUrgency(deadline: string | null | undefined): { urgency: Urgency; daysLeft: number | null } {
  if (!deadline) return { urgency: "unknown", daysLeft: null };
  const deadlineDate = new Date(deadline);
  if (isNaN(deadlineDate.getTime())) return { urgency: "unknown", daysLeft: null };

  const now = new Date();
  now.setHours(0, 0, 0, 0);
  deadlineDate.setHours(0, 0, 0, 0);

  const diffMs = deadlineDate.getTime() - now.getTime();
  const daysLeft = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (daysLeft < 0) return { urgency: "urgent", daysLeft };
  if (daysLeft < 30) return { urgency: "urgent", daysLeft };
  if (daysLeft <= 90) return { urgency: "soon", daysLeft };
  return { urgency: "ok", daysLeft };
}

const urgencyConfig: Record<Urgency, { label: string; classes: string; dot: string }> = {
  urgent: { label: "URGENT", classes: "bg-red-50 text-red-700 border-red-200", dot: "bg-red-500" },
  soon: { label: "SOON", classes: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-500" },
  ok: { label: "OK", classes: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
  unknown: { label: "TBD", classes: "bg-gray-50 text-gray-500 border-gray-200", dot: "bg-gray-400" },
};

function DeadlineBadge({ deadline }: { deadline: string | null | undefined }) {
  const { urgency, daysLeft } = computeUrgency(deadline);
  const cfg = urgencyConfig[urgency];
  const tooltip = daysLeft !== null
    ? daysLeft < 0
      ? `Deadline passed ${Math.abs(daysLeft)} days ago`
      : `${daysLeft} days remaining`
    : "No deadline data";

  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold tracking-wide rounded-full border ${cfg.classes}`}
      title={tooltip}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

// --- Eligibility gap warning chip ---

function EligibilityGapChip({ gaps }: { gaps: ProgramEvaluation["eligibility_gaps"] }) {
  const [expanded, setExpanded] = useState(false);

  if (!gaps || gaps.length === 0) return null;

  return (
    <div className="relative inline-block">
      <button
        onClick={() => setExpanded(!expanded)}
        onMouseEnter={() => setExpanded(true)}
        onMouseLeave={() => setExpanded(false)}
        className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold tracking-wide rounded-full border bg-amber-50 text-amber-700 border-amber-200 cursor-pointer"
      >
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
        </svg>
        {gaps.length} gap{gaps.length > 1 ? "s" : ""}
      </button>
      {expanded && (
        <div className="absolute z-10 left-0 top-full mt-1 w-72 p-3 bg-surface border border-space-30 rounded-xl shadow-card text-xs text-space-50 space-y-1.5 animate-[scaleIn_150ms_ease-out]">
          {gaps.map((gap, j) => (
            <p key={j}>
              <span className="font-semibold text-space">{gap.field}:</span>{" "}
              requires {gap.required_value}, has {gap.candidate_value}
              {gap.notes && <span className="text-space-40"> — {gap.notes}</span>}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Scholarship badge ---

function ScholarshipBadge({ scholarship }: { scholarship: ProgramEvaluation["scholarship"] }) {
  if (!scholarship) return null;

  const label = scholarship.amount
    ? `${scholarship.name} (${scholarship.amount})`
    : scholarship.name;

  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold tracking-wide rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200"
      title={label}
    >
      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      {label}
    </span>
  );
}

// --- Collapsible "Why?" section ---

function WhySection({ evaluation }: { evaluation: ProgramEvaluation }) {
  const [open, setOpen] = useState(false);

  const hasContent = evaluation.recommendation_rationale || evaluation.evidence_quote || evaluation.evidence_url;
  if (!hasContent) return null;

  return (
    <div className="mt-2.5">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 text-xs font-medium text-jove hover:text-jove-200 transition-colors"
      >
        <svg
          className={`h-3 w-3 transition-transform duration-200 ${open ? "rotate-90" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
        Why this rating?
      </button>
      <div
        className={`overflow-hidden transition-all duration-200 ease-in-out ${
          open ? "max-h-96 opacity-100 mt-2" : "max-h-0 opacity-0"
        }`}
      >
        <div className="pl-4 border-l-2 border-jove-50 space-y-2 text-xs text-space-50">
          {evaluation.recommendation_rationale && (
            <p className="leading-relaxed">
              <span className="font-semibold text-space">Rationale:</span>{" "}
              {evaluation.recommendation_rationale}
            </p>
          )}
          {evaluation.evidence_quote && (
            <blockquote className="italic text-space-40 border-l-2 border-space-30 pl-2.5 leading-relaxed">
              &ldquo;{evaluation.evidence_quote}&rdquo;
            </blockquote>
          )}
          {evaluation.evidence_url && (
            <a
              href={evaluation.evidence_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-jove hover:text-jove-200 truncate max-w-full"
            >
              <svg className="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
              </svg>
              <span className="truncate">{evaluation.evidence_url}</span>
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

// --- Coverage stats bar ---

function CoverageBar({ stats }: { stats: ShortlistData["coverage_stats"] }) {
  if (!stats) return null;
  const total = stats.total_programs || 0;
  if (total === 0) return null;

  const segments = [
    { count: stats.compliant, color: "bg-emerald-500", label: "Compliant" },
    { count: stats.high_risk, color: "bg-amber-400", label: "High Risk" },
    { count: stats.hard_reject, color: "bg-red-400", label: "Reject" },
  ].filter((s) => s.count > 0);

  return (
    <div className="space-y-2.5">
      {/* Visual bar */}
      <div className="flex h-2 rounded-full overflow-hidden bg-space-30/50">
        {segments.map((seg) => (
          <div
            key={seg.label}
            className={`${seg.color} transition-all duration-300`}
            style={{ width: `${(seg.count / total) * 100}%` }}
            title={`${seg.label}: ${seg.count}`}
          />
        ))}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
        {segments.map((seg) => (
          <div key={seg.label} className="flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${seg.color}`} />
            <span className="text-space-50">{seg.label}: <span className="font-medium text-space">{seg.count}</span></span>
          </div>
        ))}
        <span className="text-space-40 ml-auto font-mono tabular-nums">{total} total</span>
      </div>
    </div>
  );
}

// --- Main table component ---

export function ShortlistTable({ data }: Props) {
  return (
    <div className="p-4 space-y-4">
      {/* Coverage stats bar */}
      <CoverageBar stats={data.coverage_stats} />

      {/* Program cards */}
      <div className="space-y-2">
        {data.evaluations.map((evaluation, i) => (
          <div
            key={`${evaluation.university_name ?? evaluation.university}-${evaluation.program_name ?? evaluation.program}-${i}`}
            className="border border-space-30 rounded-xl p-3.5 hover:shadow-sm hover:border-space-40/50 transition-all duration-150 bg-surface"
          >
            {/* Row 1: University name + risk badge + deadline badge */}
            <div className="flex items-start justify-between gap-2 mb-1">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-space truncate">
                  {evaluation.university_name ?? evaluation.university}
                </p>
                <p className="text-xs text-space-50 truncate mt-0.5">{evaluation.program_name ?? evaluation.program}</p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <DeadlineBadge deadline={evaluation.application_deadline} />
                <Badge risk={evaluation.risk_level} />
              </div>
            </div>

            {/* Row 2: Meta info */}
            <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-space-40 mt-2">
              {evaluation.city && (
                <span className="flex items-center gap-1">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                  </svg>
                  {evaluation.city}, {evaluation.country}
                </span>
              )}
              {evaluation.duration && (
                <span className="flex items-center gap-1">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {evaluation.duration}
                </span>
              )}
              {evaluation.tuition && (
                <span className="flex items-center gap-1">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
                  </svg>
                  {evaluation.tuition}
                </span>
              )}
              {evaluation.intake && (
                <span className="flex items-center gap-1">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                  </svg>
                  {evaluation.intake}
                </span>
              )}
            </div>

            {/* Row 3: Chips — eligibility gaps + scholarship */}
            {(evaluation.eligibility_gaps?.length || evaluation.scholarship) && (
              <div className="flex flex-wrap gap-1.5 mt-2.5">
                <EligibilityGapChip gaps={evaluation.eligibility_gaps} />
                <ScholarshipBadge scholarship={evaluation.scholarship} />
              </div>
            )}

            {/* Collapsible "Why?" section */}
            <WhySection evaluation={evaluation} />

            {/* Program URL */}
            {evaluation.program_url && (
              <a
                href={evaluation.program_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 mt-2 text-xs text-jove hover:text-jove-200 truncate max-w-full"
              >
                <svg className="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                </svg>
                <span className="truncate">{evaluation.program_url}</span>
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
